import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileSignature,
  Building2,
  Calendar,
  DollarSign,
  Clock,
  Package,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { agreementsAPI, adminAgreementsAPI } from '../services/api';
import { useDisplay } from '../hooks/useDisplay';
import { formatMoney } from '../utils/format';

export default function AgreementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin/');
  const d = useDisplay();

  // Use admin API if on admin route
  const { data, isLoading, error } = useQuery({
    queryKey: ['agreement', id, isAdminRoute],
    queryFn: () => isAdminRoute ? adminAgreementsAPI.get(id) : agreementsAPI.get(id),
    enabled: !!id,
  });

  const agreement = data?.data?.agreement;
  const additions = data?.data?.additions || [];

  // Backend annotates additions with isActive/state on the admin route and
  // pre-filters to active-only on the customer route. Treat any addition
  // without an explicit false flag as active — this keeps rendering safe if a
  // caller ever hits the page with un-annotated data.
  const activeAdditions = additions.filter(a => a.isActive !== false);
  const inactiveAdditions = additions.filter(a => a.isActive === false);

  const extendedOf = (item) =>
    item.extPrice ?? item.extendedPrice ?? (item.quantity * item.unitPrice) ?? 0;
  const additionsSubtotal = activeAdditions.reduce((sum, item) => sum + (extendedOf(item) || 0), 0);
  const baseFee = Number(agreement?.billAmount) || 0;
  const grandTotal = baseFee + additionsSubtotal;
  const isOneTime = !!agreement?.billOneTimeFlag || !!agreement?.oneTimeFlag;
  const cycleLabel = agreement?.billingCycle?.name || agreement?.billingCycle || '';
  const totalCycleLabel = isOneTime ? 'One-Time' : cycleLabel;

  // CW stores the same applicationUnlimitedFlag=true on both truly-unlimited
  // MSP agreements and per-item on-demand agreements that don't include any
  // hours at all — the only thing that distinguishes them is whether there's
  // a recurring base fee. Treat "unlimited coverage" as real only when the
  // agreement is actually charging for it; otherwise the block field is
  // meaningless UI noise. An explicit applicationLimit still counts (a
  // prepaid block that was billed one-time can have baseFee=0 but real hours).
  const hasIncludedCoverage =
    (agreement?.applicationLimit || 0) > 0 ||
    (!!agreement?.applicationUnlimitedFlag && baseFee > 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '$0.00';
    return formatMoney(parseFloat(price));
  };

  const BackButton = () => (
    <button
      onClick={() => {
        if (isAdminRoute) {
          const returnUrl = sessionStorage.getItem('adminAgreementsReturnUrl') || '/admin/agreements';
          sessionStorage.removeItem('adminAgreementsReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        } else {
          const returnUrl = sessionStorage.getItem('agreementsReturnUrl') || '/agreements';
          sessionStorage.removeItem('agreementsReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        }
      }}
      className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-6"
    >
      <ArrowLeft className="w-4 h-4" />
      Back to Agreements
    </button>
  );

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <BackButton />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading agreement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !agreement) {
    return (
      <div className="animate-fade-in">
        <BackButton />
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">Agreement Not Found</h2>
          <p className="text-red-700 dark:text-red-300">
            {error?.response?.data?.error || 'This agreement could not be loaded.'}
          </p>
        </div>
      </div>
    );
  }

  const isActive = !agreement.cancelledFlag && !agreement.expiredFlag;
  const isCancelled = agreement.cancelledFlag;

  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <BackButton />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{agreement.name}</h1>
            <p className="text-neutral-500 dark:text-neutral-400">Agreement #{agreement.id}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            isCancelled
              ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
              : isActive
                ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300'
          }`}>
            {isCancelled ? 'Cancelled' : isActive ? 'Active' : 'Expired'}
          </span>
        </div>
      </div>

      {/* Agreement Info */}
      {d.visible('agreementDetail.section.details') && (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">{d.label('agreementDetail.section.details')}</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {d.visible('agreementDetail.details.company') && (
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.details.company')}</div>
              <div className="font-medium text-neutral-900 dark:text-white">{agreement.company?.name || 'N/A'}</div>
            </div>
          </div>
          )}
          {d.visible('agreementDetail.details.type') && (
          <div className="flex items-start gap-3">
            <FileSignature className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.details.type')}</div>
              <div className="font-medium text-neutral-900 dark:text-white">{agreement.type?.name || 'N/A'}</div>
            </div>
          </div>
          )}
          {d.visible('agreementDetail.details.startDate') && (
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.details.startDate')}</div>
              <div className="font-medium text-neutral-900 dark:text-white">{formatDate(agreement.startDate)}</div>
            </div>
          </div>
          )}
          {d.visible('agreementDetail.details.endDate') && (
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.details.endDate')}</div>
              <div className="font-medium text-neutral-900 dark:text-white">{formatDate(agreement.endDate)}</div>
            </div>
          </div>
          )}
          {d.visible('agreementDetail.details.billingCycle') && (
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.details.billingCycle')}</div>
              <div className="font-medium text-neutral-900 dark:text-white">{agreement.billingCycle?.name || agreement.periodType || 'N/A'}</div>
            </div>
          </div>
          )}
          {d.visible('agreementDetail.details.workRole') && (
          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.details.workRole')}</div>
              <div className="font-medium text-neutral-900 dark:text-white">{agreement.workRole?.name || 'N/A'}</div>
            </div>
          </div>
          )}
        </div>

        {/* Additional Info — internalNotes is CW's staff-only field
            (billing arrangements, escalation notes, etc.), so it renders on
            the admin route only. customerPO is safe to show anywhere. */}
        {((d.visible('agreementDetail.details.customerPO') && agreement.customerPO) || (isAdminRoute && agreement.internalNotes)) && (
          <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
            {d.visible('agreementDetail.details.customerPO') && agreement.customerPO && (
              <div className="mb-4">
                <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{d.label('agreementDetail.details.customerPO')}</div>
                <div className="font-medium text-neutral-900 dark:text-white">{agreement.customerPO}</div>
              </div>
            )}
            {isAdminRoute && agreement.internalNotes && (
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">Internal Notes</div>
                <p className="text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{agreement.internalNotes}</p>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Billing & Coverage — surfaces the three ways a CW agreement can carry
          value: a flat recurring fee (billAmount), a block of covered units
          (applicationLimit + applicationUnits), and additional line items
          (rendered below). Displaying each explicitly keeps different agreement
          shapes (retainer, block-time, MSP flat, on-demand) all legible. */}
      {d.visible('agreementDetail.section.billingCoverage') && (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">{d.label('agreementDetail.section.billingCoverage')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {d.visible('agreementDetail.billingCoverage.baseFee') && (
          <div className="p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
            <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{d.label('agreementDetail.billingCoverage.baseFee')}</div>
            <div className="text-lg font-bold text-neutral-900 dark:text-white">
              {baseFee > 0 ? formatPrice(baseFee) : '—'}
            </div>
            {baseFee > 0 && (
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                per {isOneTime ? 'one-time bill' : cycleLabel || 'cycle'}
              </div>
            )}
          </div>
          )}
          {d.visible('agreementDetail.billingCoverage.includedCoverage') && (
          <div className="p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
            <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{d.label('agreementDetail.billingCoverage.includedCoverage')}</div>
            <div className="text-lg font-bold text-neutral-900 dark:text-white">
              {!hasIncludedCoverage
                ? 'None (per-item billing)'
                : agreement.applicationUnlimitedFlag
                  ? `Unlimited ${agreement.applicationUnits || ''}`.trim()
                  : `${agreement.applicationLimit} ${agreement.applicationUnits || ''}`.trim()}
            </div>
            {hasIncludedCoverage && !agreement.applicationUnlimitedFlag && agreement.applicationCycleValue && (
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                per {agreement.applicationCycleValue}
              </div>
            )}
          </div>
          )}
          {d.visible('agreementDetail.billingCoverage.overage') && (
          <div className="p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
            <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{d.label('agreementDetail.billingCoverage.overage')}</div>
            <div className="text-sm font-medium text-neutral-900 dark:text-white space-y-0.5 mt-1">
              {d.visible('agreementDetail.billingCoverage.overage.carryOver') && (
                <div className="flex items-center gap-1.5">
                  {agreement.carryOverUnused ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-neutral-400" />}
                  <span>{d.label('agreementDetail.billingCoverage.overage.carryOver')}</span>
                </div>
              )}
              {d.visible('agreementDetail.billingCoverage.overage.overruns') && (
                <div className="flex items-center gap-1.5">
                  {agreement.allowOverruns ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-neutral-400" />}
                  <span>{d.label('agreementDetail.billingCoverage.overage.overruns')}</span>
                </div>
              )}
            </div>
          </div>
          )}
          {d.visible('agreementDetail.billingCoverage.term') && (
          <div className="p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
            <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{d.label('agreementDetail.billingCoverage.term')}</div>
            <div className="text-sm font-medium text-neutral-900 dark:text-white space-y-0.5 mt-1">
              {d.visible('agreementDetail.billingCoverage.term.noEndDate') && (
                <div className="flex items-center gap-1.5">
                  {agreement.noEndingDateFlag ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-neutral-400" />}
                  <span>{d.label('agreementDetail.billingCoverage.term.noEndDate')}</span>
                </div>
              )}
              {d.visible('agreementDetail.billingCoverage.term.oneTime') && (
                <div className="flex items-center gap-1.5">
                  {isOneTime ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-neutral-400" />}
                  <span>{d.label('agreementDetail.billingCoverage.term.oneTime')}</span>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
      )}

      {/* Active Additions */}
      {d.visible('agreementDetail.section.items') && (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {d.label('agreementDetail.section.items')} ({activeAdditions.length})
          </h2>
        </div>

        {activeAdditions.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
            <Package className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
            <p>No active items on this agreement</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                  <th className="text-left px-6 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.items.col.item')}</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.items.col.qty')}</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.items.col.unitPrice')}</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">{d.label('agreementDetail.items.col.extended')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {activeAdditions.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-neutral-900 dark:text-white">
                        {item.product?.description || item.description || 'Item'}
                      </div>
                      {item.product?.identifier && (
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">
                          {item.product.identifier}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center text-neutral-600 dark:text-neutral-300">
                      {item.quantity || 1}
                    </td>
                    <td className="px-4 py-4 text-right text-neutral-600 dark:text-neutral-300">
                      {formatPrice(item.unitPrice)}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-neutral-900 dark:text-white">
                      {formatPrice(extendedOf(item))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Agreement Total — shown whenever the agreement carries value, even if
          there are no additions (e.g. flat-fee MSP, block-time retainer). */}
      {d.visible('agreementDetail.section.total') && (baseFee > 0 || activeAdditions.length > 0) && (
        <div className="mt-6 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">{d.label('agreementDetail.section.total')}</h2>
          <div className="space-y-2">
            {d.visible('agreementDetail.total.baseFee') && baseFee > 0 && (
              <div className="flex justify-between text-neutral-700 dark:text-neutral-300">
                <span>{d.label('agreementDetail.total.baseFee')}</span>
                <span>{formatPrice(baseFee)}</span>
              </div>
            )}
            {d.visible('agreementDetail.total.additionsSubtotal') && activeAdditions.length > 0 && (
              <div className="flex justify-between text-neutral-700 dark:text-neutral-300">
                <span>{d.label('agreementDetail.total.additionsSubtotal')} ({activeAdditions.length} {activeAdditions.length === 1 ? 'item' : 'items'})</span>
                <span>{formatPrice(additionsSubtotal)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-3 border-t border-neutral-200 dark:border-neutral-700">
              <span className="font-medium text-neutral-900 dark:text-white">
                {d.label('agreementDetail.total.grandTotal')}{totalCycleLabel ? ` (${totalCycleLabel})` : ''}
              </span>
              <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {formatPrice(grandTotal)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Historical (cancelled/expired) additions — admin route only.
          Customer route pre-filters these out server-side. */}
      {inactiveAdditions.length > 0 && (
        <div className="mt-6 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Historical Items ({inactiveAdditions.length})
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              Cancelled, expired, or not yet effective — not counted in the agreement total.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                  <th className="text-left px-6 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Item</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Status</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Qty</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Unit Price</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Extended</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {inactiveAdditions.map((item) => {
                  const state = item.state || 'inactive';
                  const stateClass = state === 'cancelled'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    : state === 'expired'
                      ? 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
                  const stateLabel = state.charAt(0).toUpperCase() + state.slice(1);
                  return (
                    <tr key={item.id} className="text-neutral-500 dark:text-neutral-400">
                      <td className="px-6 py-4">
                        <div className="font-medium line-through decoration-neutral-400/60">
                          {item.product?.description || item.description || 'Item'}
                        </div>
                        {item.product?.identifier && (
                          <div className="text-sm">{item.product.identifier}</div>
                        )}
                        {(item.cancelledDate || item.endDate || item.effectiveDate) && (
                          <div className="text-xs mt-1">
                            {item.cancelledDate && <>Cancelled {formatDate(item.cancelledDate)}</>}
                            {!item.cancelledDate && item.endDate && <>Ended {formatDate(item.endDate)}</>}
                            {!item.cancelledDate && !item.endDate && item.effectiveDate && <>Effective {formatDate(item.effectiveDate)}</>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stateClass}`}>
                          {stateLabel}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">{item.quantity || 1}</td>
                      <td className="px-4 py-4 text-right">{formatPrice(item.unitPrice)}</td>
                      <td className="px-6 py-4 text-right line-through decoration-neutral-400/60">
                        {formatPrice(extendedOf(item))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
